import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useRef, useState, useCallback } from 'react';
import { useId, useClickOutside } from '@mantine/hooks';
import { getDefaultZIndex, useComponentDefaultProps, useMantineTheme } from '@mantine/styles';
import { usePopover } from './use-popover.js';
import { PopoverContextProvider } from './Popover.context.js';
import { PopoverTarget } from './PopoverTarget/PopoverTarget.js';
import { PopoverDropdown } from './PopoverDropdown/PopoverDropdown.js';
import { getFloatingPosition } from '../Floating/get-floating-position/get-floating-position.js';
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;

var __objRest = function __objRest(source, exclude) {
  var target = {};

  for (var prop in source) {
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0) target[prop] = source[prop];
  }

  if (source != null && __getOwnPropSymbols) {
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = __getOwnPropSymbols(source)[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var prop = _step.value;
        if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop)) target[prop] = source[prop];
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator["return"] != null) {
          _iterator["return"]();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }
  }

  return target;
};

var defaultProps = {
  position: "bottom",
  offset: 8,
  positionDependencies: [],
  transitionProps: {
    transition: "fade",
    duration: 150
  },
  middlewares: {
    flip: true,
    shift: true,
    inline: false
  },
  arrowSize: 7,
  arrowOffset: 5,
  arrowRadius: 0,
  arrowPosition: "side",
  closeOnClickOutside: true,
  withinPortal: false,
  closeOnEscape: true,
  trapFocus: false,
  withRoles: true,
  returnFocus: false,
  clickOutsideEvents: ["mousedown", "touchstart"],
  zIndex: getDefaultZIndex("popover"),
  __staticSelector: "Popover",
  width: "max-content"
};

function Popover(props) {
  var _b, _c, _d, _e, _f, _g;

  var arrowRef = useRef(null);

  var _a = useComponentDefaultProps("Popover", defaultProps, props),
      children = _a.children,
      position = _a.position,
      offset = _a.offset,
      onPositionChange = _a.onPositionChange,
      positionDependencies = _a.positionDependencies,
      opened = _a.opened,
      transitionProps = _a.transitionProps,
      width = _a.width,
      middlewares = _a.middlewares,
      withArrow = _a.withArrow,
      arrowSize = _a.arrowSize,
      arrowOffset = _a.arrowOffset,
      arrowRadius = _a.arrowRadius,
      arrowPosition = _a.arrowPosition,
      unstyled = _a.unstyled,
      classNames = _a.classNames,
      styles = _a.styles,
      closeOnClickOutside = _a.closeOnClickOutside,
      withinPortal = _a.withinPortal,
      portalProps = _a.portalProps,
      closeOnEscape = _a.closeOnEscape,
      clickOutsideEvents = _a.clickOutsideEvents,
      trapFocus = _a.trapFocus,
      onClose = _a.onClose,
      onOpen = _a.onOpen,
      onChange = _a.onChange,
      zIndex = _a.zIndex,
      radius = _a.radius,
      shadow = _a.shadow,
      id = _a.id,
      defaultOpened = _a.defaultOpened,
      __staticSelector = _a.__staticSelector,
      withRoles = _a.withRoles,
      disabled = _a.disabled,
      returnFocus = _a.returnFocus,
      variant = _a.variant,
      keepMounted = _a.keepMounted,
      others = __objRest(_a, ["children", "position", "offset", "onPositionChange", "positionDependencies", "opened", "transitionProps", "width", "middlewares", "withArrow", "arrowSize", "arrowOffset", "arrowRadius", "arrowPosition", "unstyled", "classNames", "styles", "closeOnClickOutside", "withinPortal", "portalProps", "closeOnEscape", "clickOutsideEvents", "trapFocus", "onClose", "onOpen", "onChange", "zIndex", "radius", "shadow", "id", "defaultOpened", "__staticSelector", "withRoles", "disabled", "returnFocus", "variant", "keepMounted"]);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      targetNode = _useState2[0],
      setTargetNode = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      dropdownNode = _useState4[0],
      setDropdownNode = _useState4[1];

  var uid = useId(id);
  var theme = useMantineTheme();
  var popover = usePopover({
    middlewares: middlewares,
    width: width,
    position: getFloatingPosition(theme.dir, position),
    offset: typeof offset === "number" ? offset + (withArrow ? arrowSize / 2 : 0) : offset,
    arrowRef: arrowRef,
    arrowOffset: arrowOffset,
    onPositionChange: onPositionChange,
    positionDependencies: positionDependencies,
    opened: opened,
    defaultOpened: defaultOpened,
    onChange: onChange,
    onOpen: onOpen,
    onClose: onClose
  });
  useClickOutside(function () {
    return popover.opened && closeOnClickOutside && popover.onClose();
  }, clickOutsideEvents, [targetNode, dropdownNode]);
  var reference = useCallback(function (node) {
    setTargetNode(node);
    popover.floating.reference(node);
  }, [popover.floating.reference]);
  var floating = useCallback(function (node) {
    setDropdownNode(node);
    popover.floating.floating(node);
  }, [popover.floating.floating]);
  return (
    /* @__PURE__ */
    React.createElement(PopoverContextProvider, {
      value: {
        returnFocus: returnFocus,
        disabled: disabled,
        controlled: popover.controlled,
        reference: reference,
        floating: floating,
        x: popover.floating.x,
        y: popover.floating.y,
        arrowX: (_d = (_c = (_b = popover.floating) == null ? void 0 : _b.middlewareData) == null ? void 0 : _c.arrow) == null ? void 0 : _d.x,
        arrowY: (_g = (_f = (_e = popover.floating) == null ? void 0 : _e.middlewareData) == null ? void 0 : _f.arrow) == null ? void 0 : _g.y,
        opened: popover.opened,
        arrowRef: arrowRef,
        transitionProps: transitionProps,
        width: width,
        withArrow: withArrow,
        arrowSize: arrowSize,
        arrowOffset: arrowOffset,
        arrowRadius: arrowRadius,
        arrowPosition: arrowPosition,
        placement: popover.floating.placement,
        trapFocus: trapFocus,
        withinPortal: withinPortal,
        portalProps: portalProps,
        zIndex: zIndex,
        radius: radius,
        shadow: shadow,
        closeOnEscape: closeOnEscape,
        onClose: popover.onClose,
        onToggle: popover.onToggle,
        getTargetId: function getTargetId() {
          return "".concat(uid, "-target");
        },
        getDropdownId: function getDropdownId() {
          return "".concat(uid, "-dropdown");
        },
        withRoles: withRoles,
        targetProps: others,
        __staticSelector: __staticSelector,
        classNames: classNames,
        styles: styles,
        unstyled: unstyled,
        variant: variant,
        keepMounted: keepMounted
      }
    }, children)
  );
}

Popover.Target = PopoverTarget;
Popover.Dropdown = PopoverDropdown;
Popover.displayName = "@mantine/core/Popover";
export { Popover };